
import { useAuth } from "@/components/AuthProvider";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { Activity, Calendar, TrendingUp, ClipboardCheck, Dumbbell, Apple } from "lucide-react";

import { StatCard } from "./overview/StatCard";
import { ProgressChart } from "./overview/ProgressChart";
import { WeightChart } from "./overview/WeightChart";
import { UpcomingEvents } from "./overview/UpcomingEvents";
import { DashboardHeader } from "./overview/DashboardHeader";
import { TrainerInfo } from "./overview/TrainerInfo";
import { useStudentData } from "./overview/hooks/useStudentData";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { DashboardSkeleton } from "@/components/shared/DashboardSkeleton";

export const StudentDashboardOverview = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslations("student");
  const dateLocale = language === 'pt' ? ptBR : enUS;
  
  const { data: studentData, isLoading: isLoadingStudent } = useStudentData(user?.id, user?.email);

  if (isLoadingStudent) {
    return <DashboardSkeleton />;
  }

  if (!studentData) {
    return (
      <div className="p-4">
        <h2 className="text-3xl font-bold tracking-tight">Bem-vindo!</h2>
        <p className="text-muted-foreground mt-2">
          Parece que a sua conta ainda não está associada a nenhum personal trainer.
          Por favor, confirme se o seu email está correto ou contacte o seu personal trainer.
        </p>
      </div>
    );
  }

  // Ordenar avaliações físicas por data (mais recente primeiro)
  const sortedAssessments = studentData.physical_assessments 
    ? [...studentData.physical_assessments].sort((a, b) => 
        new Date(b.assessment_date).getTime() - new Date(a.assessment_date).getTime()
      )
    : [];
    
  const latestAssessment = sortedAssessments.length > 0 ? sortedAssessments[0] : null;
  
  // Verificar se há alguma aula agendada exclusivamente para este aluno
  const hasUpcomingEvents = studentData.upcomingEvents && studentData.upcomingEvents.length > 0;
  const nextEvent = hasUpcomingEvents ? studentData.upcomingEvents[0] : null;

  // Obter próxima sessão de treino com base apenas em eventos agendados para este aluno
  const nextSession = nextEvent && nextEvent.event_type === 'training_session' 
    ? { 
        date: nextEvent.start_time,
        title: nextEvent.title || "Aula agendada"
      } 
    : null;

  // Formatar a data da próxima aula no formato "segunda-feira 10/02 - 16h"
  const formattedNextSessionDate = nextSession ? (
    `${format(new Date(nextSession.date), "EEEE dd/MM", { locale: dateLocale })} - ${format(new Date(nextSession.date), "HH'h'", { locale: dateLocale })}`
  ) : t("overview.cards.noClassesScheduled");

  // Capitalizar a primeira letra do dia da semana
  const capitalizedNextSessionDate = nextSession
    ? formattedNextSessionDate.charAt(0).toUpperCase() + formattedNextSessionDate.slice(1)
    : formattedNextSessionDate;

  // Process training sessions data to get weight information
  const processTrainingData = (sessions: any[]) => {
    if (!sessions) return [];
    
    return sessions
      ?.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      ?.map((session: any) => {
        const maxWeight = session.training_session_exercises?.reduce((max: number, exercise: any) => {
          const weight = exercise.weight ? parseFloat(exercise.weight) : 0;
          return weight > max ? weight : max;
        }, 0);

        return {
          date: format(new Date(session.created_at), "dd/MM/yyyy", { locale: dateLocale }),
          pesoMáximo: maxWeight || 0,
          séries: session.training_session_exercises?.reduce((total: number, ex: any) => 
            total + (ex.sets_completed || 0), 0)
        };
      }) || [];
  };

  return (
    <div className="space-y-6">
      <DashboardHeader />
      
      <div className="mb-6">
        <TrainerInfo trainer={studentData.trainer} />
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard 
          title={t("overview.cards.nextClass")}
          value={capitalizedNextSessionDate}
          description={nextSession?.title || t("overview.cards.noClassesScheduled")}
          icon={<Calendar className="h-4 w-4 text-flame" />}
          linkTo="/student-dashboard/schedule"
        />
        
        <StatCard 
          title={t("overview.cards.completedWorkouts")}
          value={studentData?.completedSessions || 0}
          description={t("overview.cards.totalWorkouts")}
          icon={<Dumbbell className="h-4 w-4 text-flame" />}
          linkTo="/student-dashboard/training-sessions"
        />
        
        <StatCard 
          title={t("overview.cards.fitnessLevel")}
          value={studentData?.fitness_level || t("overview.cards.notDefined")}
          description={t("overview.cards.currentLevel")}
          icon={<Activity className="h-4 w-4 text-flame" />}
          linkTo="/student-dashboard/progress"
        />
        
        <StatCard 
          title={t("overview.cards.lastAssessment")}
          value={latestAssessment 
            ? format(new Date(latestAssessment.assessment_date), "dd/MM/yyyy")
            : t("overview.cards.notPerformed")}
          description={latestAssessment
            ? `${t("overview.cards.weight")}: ${latestAssessment.weight}kg`
            : t("overview.cards.noData")}
          icon={<ClipboardCheck className="h-4 w-4 text-flame" />}
          linkTo="/student-dashboard/assessments"
        />
        
        <StatCard 
          title={t("overview.cards.goals")}
          value={t("overview.cards.inProgress")}
          description={studentData?.goals || t("overview.cards.goalsNotDefined")}
          icon={<TrendingUp className="h-4 w-4 text-flame" />}
          linkTo="/student-dashboard/settings"
        />
        
        <StatCard 
          title={t("overview.cards.trainingType")}
          value={studentData?.preferred_training_type || t("overview.cards.notDefined")}
          description={t("overview.cards.preferredType")}
          icon={<Apple className="h-4 w-4 text-flame" />}
          linkTo="/student-dashboard/workouts"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ProgressChart data={studentData.progressData} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <WeightChart data={studentData.weightData} />
        <UpcomingEvents events={studentData.upcomingEvents} />
      </div>
    </div>
  );
};
